import { Link } from "react-router-dom";
import { Heart, Mail, MapPin, MessageCircle } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";

function formatWa(n) {
  const digits = (n || "").replace(/[^0-9]/g, "");
  if (digits.length === 11) return `+${digits.slice(0,1)} ${digits.slice(1,4)} ${digits.slice(4,7)} ${digits.slice(7)}`;
  return `+${digits}`;
}

export default function Footer() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const showTarifs = user && (settings.tarifs_visible || isAdmin);
  const waNumber = (settings.whatsapp_number || "15142389008").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${waNumber}?text=${encodeURIComponent("Bonjour " + (settings.app_name || "Sama Écoute"))}`;
  return (
    <footer className="bg-ink text-bone mt-20" data-testid="site-footer">
      <div className="container-jamm py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img
                src={settings.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url}
                alt={settings.app_name || "Sama Écoute"}
                className="h-24 w-auto object-contain bg-bone/95 rounded-2xl p-2 shadow-md"
                data-testid="footer-logo-img"
              />
            ) : (
              <>
                <span className="grid place-items-center w-10 h-10 rounded-full bg-gold text-ink font-display text-2xl">{(settings.app_name || "S")[0]}</span>
                <span className="font-display text-3xl">{settings.app_name || "Sama Écoute"}</span>
              </>
            )}
          </div>
          <p className="text-bone/70 mt-4 leading-relaxed text-sm">
            Une parole bienveillante, partout dans le monde. Pour le Sénégal, l'Afrique et la diaspora.
          </p>
          <div className="flex items-center gap-2 text-bone/60 text-xs mt-5">
            <Heart className="w-3.5 h-3.5 text-gold" /> Fait avec humanité — Sénégal & Diaspora
          </div>
        </div>
        <div>
          <div className="overline text-gold mb-4">Navigation</div>
          <ul className="space-y-2 text-bone/80 text-sm">
            <li><Link to="/accompagnateurs" className="hover:text-gold transition">Nos accompagnateurs</Link></li>
            {showTarifs && <li><Link to="/tarifs" className="hover:text-gold transition">Tarifs</Link></li>}
            <li><Link to="/confidentialite" className="hover:text-gold transition">Confidentialité</Link></li>
            <li><Link to="/login" className="hover:text-gold transition">Se connecter</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline text-gold mb-4">Soutien</div>
          <ul className="space-y-2 text-bone/80 text-sm">
            <li><a href="#faq" className="hover:text-gold transition">FAQ</a></li>
            <li><a href="#contact" className="hover:text-gold transition">Contact</a></li>
            <li><a href={waHref} target="_blank" rel="noreferrer" className="hover:text-gold transition">WhatsApp</a></li>
          </ul>
        </div>
        <div>
          <div className="overline text-gold mb-4">Contact</div>
          <ul className="space-y-3 text-bone/80 text-sm">
            <li className="flex gap-2 items-start" data-testid="footer-contact-email">
              <Mail className="w-4 h-4 mt-0.5 text-gold shrink-0" />
              <a href={`mailto:${settings.contact_email}`} className="hover:text-gold transition break-all">{settings.contact_email}</a>
            </li>
            <li className="flex gap-2 items-start" data-testid="footer-contact-whatsapp">
              <MessageCircle className="w-4 h-4 mt-0.5 text-gold shrink-0" />
              <a href={waHref} target="_blank" rel="noreferrer" className="hover:text-gold transition">WhatsApp · {formatWa(waNumber)}</a>
            </li>
            <li className="flex gap-2 items-start"><MapPin className="w-4 h-4 mt-0.5 text-gold shrink-0" /> {settings.contact_location}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-jamm py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-bone/50">
          <span>© {new Date().getFullYear()} {settings.app_name || "Sama Écoute"}. Une plateforme d'écoute, pas un service médical.</span>
          <span>Confidentialité · Conditions · Cookies</span>
        </div>
      </div>
    </footer>
  );
}
